'use client';

import React, { useRef, useState, useCallback, JSX } from 'react';
import { useEvent } from 'react-use';

// Import all audio events
import {
    AUDIO_EVENTS,
    PlayEvent,
    PauseEvent,
    TimeUpdateEvent,
    SeekEvent,
    EndedEvent,
    VolumeChangedEvent,
} from '../../events/AudioEvents';

/**
 * AudioPlayer Component
 *
 * A global/hidden component that wraps an audio element and handles class-based window events
 * for audio playback. This ensures only one audio source plays at a time and provides
 * event-based communication for other components.
 */
export default function AudioPlayer(): JSX.Element {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [currentSrc, setCurrentSrc] = useState<string>('');

    const handleTimeUpdate = useCallback(() => {
        const audio = audioRef.current;
        if (audio && currentSrc) {
            const timeUpdateEvent = new TimeUpdateEvent(currentSrc, audio.currentTime);
            window.dispatchEvent(timeUpdateEvent);
        }
    }, [currentSrc]);

    const handleEnded = useCallback(() => {
        if (currentSrc) {
            const endedEvent = new EndedEvent(currentSrc);
            window.dispatchEvent(endedEvent);
        }

        if (audioRef.current) {
            audioRef.current.currentTime = 0;
        }
    }, [currentSrc]);

    useEvent(AUDIO_EVENTS.VOLUME_CHANGED, (event: Event) => {
        const volumeChangedEvent = event as VolumeChangedEvent;
        const src = volumeChangedEvent.src;
        const currVolume = volumeChangedEvent.volume;

        if (audioRef.current && [src, ''].includes(currentSrc)) {
            audioRef.current.volume = currVolume;
        }
    });

    useEvent(AUDIO_EVENTS.PLAY, (event: Event) => {
        const playEvent = event as PlayEvent;
        const src = playEvent.src;
        const time = playEvent.time || 0;

        if (audioRef.current) {
            if (src === currentSrc) {
                void audioRef.current.play();
            } else {
                // If src doesn't match, update src, time, and play
                audioRef.current.src = src;
                audioRef.current.currentTime = time;
                void audioRef.current.play();
                setCurrentSrc(src);
            }
        }
    });

    useEvent(AUDIO_EVENTS.PAUSE, (event: Event) => {
        const pauseEvent = event as PauseEvent;
        const src = pauseEvent.src;

        // Only pause if the source matches our current source
        if (audioRef.current && src === currentSrc) {
            audioRef.current.pause();
        }
    });

    useEvent(AUDIO_EVENTS.SEEK, (event: Event) => {
        const seekEvent = event as SeekEvent;
        const src = seekEvent.src;
        const time = seekEvent.time || 0;

        // Alternative is for seek we just update timestamp
        if (audioRef.current) {
            // If src matches current playing src, update time and play
            if (src === currentSrc) {
                audioRef.current.currentTime = time;
                void audioRef.current.play();
            } else {
                // If src doesn't match, update src, time, and play
                audioRef.current.src = src;
                audioRef.current.currentTime = time;
                void audioRef.current.play();
                setCurrentSrc(src);
            }
        }
    });

    // eslint-disable-next-line react-hooks/refs
    useEvent('timeupdate', handleTimeUpdate, audioRef.current);
    // eslint-disable-next-line react-hooks/refs
    useEvent('ended', handleEnded, audioRef.current);

    return (
        <audio id="audio-player" ref={audioRef} className="hidden">
            <source type="audio/mpeg" />
        </audio>
    );
}
